import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  getLead,
  updateLead,
  deleteLead,
  generateDraft,
  generateSequence,
  generateLinkedinNote,
  sendEmail,
  findEmail,
  listActivities,
  addActivity,
  STAGES,
} from "@/lib/api";
import { ArrowLeft, Copy, Loader2, Send, Trash2, Wand2, Calendar, MessageSquare, Pencil, X, Check, Linkedin, ExternalLink, Search } from "lucide-react";
import { toast } from "sonner";

export default function LeadDetail() {
  const { id } = useParams();
  const [lead, setLead] = useState(null);
  const [activities, setActivities] = useState([]);
  const [draftLoading, setDraftLoading] = useState(false);
  const [seqLoading, setSeqLoading] = useState(false);
  const [sendLoading, setSendLoading] = useState(false);
  const [toAddr, setToAddr] = useState("");
  const [note, setNote] = useState("");
  const [draftEditing, setDraftEditing] = useState(false);
  const [draftSubject, setDraftSubject] = useState("");
  const [draftBody, setDraftBody] = useState("");
  const [seqEditingStep, setSeqEditingStep] = useState(null);
  const [seqBody, setSeqBody] = useState("");
  const [draftSaving, setDraftSaving] = useState(false);
  const [seqSaving, setSeqSaving] = useState(false);
  const [liLoading, setLiLoading] = useState(false);
  const [liEditing, setLiEditing] = useState(false);
  const [liBody, setLiBody] = useState("");
  const [liSaving, setLiSaving] = useState(false);
  const [findingEmail, setFindingEmail] = useState(false);

  const load = async () => {
    const [l, acts] = await Promise.all([getLead(id), listActivities(id)]);
    setLead(l);
    setActivities(acts);
  };

  useEffect(() => {
    load().catch(() => toast.error("Could not load lead."));
  }, [id]);

  useEffect(() => {
    if (lead?.email) {
      const v = lead.email.toLowerCase().startsWith("guess:") ? lead.email.slice(6).trim() : lead.email;
      setToAddr(v);
    } else {
      setToAddr("");
    }
  }, [lead?.id, lead?.email]);

  const onStageChange = async (stage) => {
    setLead((l) => ({ ...l, stage }));
    try {
      await updateLead(id, { stage });
      toast.success(`Stage → ${stage}`);
      load();
    } catch {
      toast.error("Could not update stage");
    }
  };

  const onGenerateDraft = async () => {
    setDraftLoading(true);
    try {
      const l = await generateDraft(id);
      setLead(l);
      toast.success("Draft written.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Draft failed");
    } finally {
      setDraftLoading(false);
    }
  };

  const onGenerateSeq = async () => {
    setSeqLoading(true);
    try {
      const l = await generateSequence(id);
      setLead(l);
      toast.success("Follow-up sequence ready.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sequence failed");
    } finally {
      setSeqLoading(false);
    }
  };

  const onCopy = (txt) => {
    navigator.clipboard.writeText(txt);
    toast.success("Copied to clipboard.");
  };

  const onEditDraft = () => {
    setDraftSubject(lead?.draft_email?.subject || "");
    setDraftBody(lead?.draft_email?.body || "");
    setDraftEditing(true);
  };

  const onCancelDraft = () => {
    setDraftEditing(false);
    setDraftSubject("");
    setDraftBody("");
  };

  const onSaveDraft = async () => {
    setDraftSaving(true);
    try {
      const next = {
        subject: draftSubject.trim(),
        body: draftBody.trim(),
        generated_at: lead?.draft_email?.generated_at || new Date().toISOString(),
        edited_at: new Date().toISOString(),
      };
      const l = await updateLead(id, { draft_email: next });
      setLead(l);
      setDraftEditing(false);
      toast.success("Draft saved.");
      await addActivity(id, { kind: "draft_edited", body: `Edited: ${next.subject}` }).catch(() => {});
      load();
    } catch {
      toast.error("Could not save draft");
    } finally {
      setDraftSaving(false);
    }
  };

  const onEditStep = (step) => {
    setSeqEditingStep(step.step);
    setSeqBody(step.body || "");
  };

  const onCancelStep = () => {
    setSeqEditingStep(null);
    setSeqBody("");
  };

  const onSaveStep = async (step) => {
    setSeqSaving(true);
    try {
      const nextSequence = (lead.sequence || []).map((s) =>
        s.step === step.step ? { ...s, body: seqBody.trim() } : s
      );
      const l = await updateLead(id, { sequence: nextSequence });
      setLead(l);
      setSeqEditingStep(null);
      setSeqBody("");
      toast.success(`Step ${step.step} saved.`);
    } catch {
      toast.error("Could not save step");
    } finally {
      setSeqSaving(false);
    }
  };

  const onGenerateLinkedin = async () => {
    setLiLoading(true);
    try {
      const l = await generateLinkedinNote(id);
      setLead(l);
      toast.success(`Connect note ready (${l.linkedin_note?.char_count} chars).`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "LinkedIn note failed");
    } finally {
      setLiLoading(false);
    }
  };

  const onEditLinkedin = () => {
    setLiBody(lead?.linkedin_note?.body || "");
    setLiEditing(true);
  };

  const onCancelLinkedin = () => {
    setLiEditing(false);
    setLiBody("");
  };

  const onSaveLinkedin = async () => {
    setLiSaving(true);
    try {
      const next = {
        ...(lead.linkedin_note || {}),
        body: liBody.trim(),
        char_count: liBody.trim().length,
        edited_at: new Date().toISOString(),
      };
      const l = await updateLead(id, { linkedin_note: next });
      setLead(l);
      setLiEditing(false);
      toast.success("Note saved.");
    } catch {
      toast.error("Could not save note");
    } finally {
      setLiSaving(false);
    }
  };

  const onCopyAndOpenLinkedin = async () => {
    const note = lead?.linkedin_note?.body || "";
    const url = lead?.linkedin_note?.profile_url || "https://www.linkedin.com/feed/";
    if (!note) {
      toast.error("Generate a note first.");
      return;
    }
    try {
      await navigator.clipboard.writeText(note);
    } catch (err) {
      console.warn("Clipboard write blocked; user can still copy manually.", err);
    }
    window.open(url, "_blank", "noopener,noreferrer");
    toast.success("Note copied. Hit Connect → Add a note on LinkedIn.");
  };

  const onMarkConnected = async () => {
    await addActivity(id, {
      kind: "linkedin_sent",
      body: `Connect request sent on LinkedIn`,
    });
    if (lead.stage === "new" || lead.stage === "researching") {
      await updateLead(id, { stage: "contacted" });
    }
    toast.success("Logged LinkedIn outreach.");
    load();
  };

  const onFindEmail = async () => {
    setFindingEmail(true);
    try {
      const l = await findEmail(id);
      setLead(l);
      toast.success(`Found: ${l.email} (${l.email_status || "no status"})`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No verified email found");
    } finally {
      setFindingEmail(false);
    }
  };

  const onAddNote = async () => {
    if (!note.trim()) return;
    try {
      await addActivity(id, { kind: "note", body: note.trim() });
      setNote("");
      const acts = await listActivities(id);
      setActivities(acts);
      toast.success("Note added.");
    } catch {
      toast.error("Could not add note");
    }
  };

  const onMarkSent = async () => {
    await addActivity(id, { kind: "email_sent", body: lead.draft_email?.subject || "Outreach sent (manual)" });
    if (lead.stage === "new" || lead.stage === "researching") {
      await updateLead(id, { stage: "contacted" });
    }
    toast.success("Logged as sent (manual).");
    load();
  };

  const onSendNow = async () => {
    const recipient = (toAddr || "").trim();
    if (!recipient || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(recipient)) {
      toast.error("Enter a valid recipient email first.");
      return;
    }
    if (!lead.draft_email?.subject || !lead.draft_email?.body) {
      toast.error("Generate a draft first.");
      return;
    }
    if (!window.confirm(`Send to ${recipient} from your Gmail?`)) return;
    setSendLoading(true);
    try {
      const l = await sendEmail(id, { to: recipient });
      setLead(l);
      toast.success(`Sent to ${recipient}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setSendLoading(false);
    }
  };

  const onDelete = async () => {
    if (!window.confirm("Delete this prospect?")) return;
    await deleteLead(id);
    toast.success("Deleted.");
    window.location.href = "/app/pipeline";
  };

  if (!lead) {
    return (
      <div className="px-12 py-10 text-[#8a8a8a]" data-testid="lead-loading">
        <Loader2 className="animate-spin" /> Loading…
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Top bar */}
      <div className="px-12 py-6 border-b border-[#222] flex items-center justify-between">
        <Link to="/app/pipeline" data-testid="back-to-pipeline" className="uppercase-mono text-[#8a8a8a] hover:text-[#CCFF00] inline-flex items-center gap-2">
          <ArrowLeft size={14} strokeWidth={1.5} /> back to pipeline
        </Link>
        <button onClick={onDelete} data-testid="lead-delete-btn" className="uppercase-mono text-[#8a8a8a] hover:text-[#FF3333] inline-flex items-center gap-2">
          <Trash2 size={14} strokeWidth={1.5} /> delete
        </button>
      </div>

      {/* Hero (editorial) */}
      <header className="px-12 py-12 border-b border-[#222] grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-8">
          <div className="uppercase-mono text-[#8a8a8a]">
            Prospect · {lead.industry || "—"} · {lead.location || "—"}
          </div>
          <h1 className="font-serif text-5xl lg:text-7xl mt-3 leading-[1.05]" data-testid="lead-name">
            {lead.name}
          </h1>
          <div className="font-mono text-sm text-[#ededed] mt-4">
            {lead.role}
            {lead.role && lead.company ? " · " : ""}
            <span className="text-[#8a8a8a]">{lead.company}</span>
          </div>
          {lead.fit_reason && (
            <p className="font-serif text-2xl text-[#ededed] mt-6 max-w-3xl leading-snug">&ldquo;{lead.fit_reason}&rdquo;</p>
          )}
          <div className="flex flex-wrap gap-2 mt-6">
            {lead.source === "tavily" && (
              <span className="text-[10px] uppercase tracking-widest bg-[#CCFF00] text-black px-2 py-1">
                ✓ Tavily-verified · {lead.source_urls?.length || 0} sources
              </span>
            )}
            {lead.source === "ai" && (
              <span className="text-[10px] uppercase tracking-widest border border-[#FFB800] text-[#FFB800] px-2 py-1">
                AI-generated · unverified
              </span>
            )}
            {lead.signals?.map((s, i) => (
              <span key={i} className="text-[10px] uppercase tracking-widest border border-[#222] px-2 py-1 text-[#8a8a8a]">
                {s}
              </span>
            ))}
          </div>
          {lead.source_urls?.length > 0 && (
            <div className="mt-6" data-testid="evidence-block">
              <div className="uppercase-mono text-[#8a8a8a] mb-2">Evidence ({lead.source_urls.length})</div>
              <ul className="space-y-1">
                {lead.source_urls.map((u, i) => (
                  <li key={u}>
                    <a
                      href={u}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[12px] font-mono text-[#CCFF00] hover:underline break-all"
                      data-testid={`evidence-link-${i}`}
                    >
                      ↗ {u}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        <div className="lg:col-span-4 border border-[#222] p-6 self-start">
          <Meta k="Fit score" v={<span className="text-[#CCFF00] font-mono text-2xl">{lead.fit_score}</span>} />
          <div className="py-2 border-b border-[#222]">
            <div className="flex items-baseline justify-between">
              <span className="uppercase-mono text-[#8a8a8a]">Email</span>
              {!lead.email && (
                <button
                  onClick={onFindEmail}
                  disabled={findingEmail}
                  data-testid="find-email-btn"
                  className="uppercase-mono text-[#CCFF00] hover:underline inline-flex items-center gap-1 disabled:opacity-50"
                >
                  {findingEmail ? <Loader2 size={11} className="animate-spin" /> : <Search size={11} />}
                  {findingEmail ? "finding…" : "find via Hunter"}
                </button>
              )}
            </div>
            {lead.email ? (
              <div className="mt-1 flex items-baseline justify-between gap-2">
                <span className="text-sm text-[#ededed] truncate" data-testid="lead-email">{lead.email}</span>
                <span
                  className={`text-[10px] uppercase tracking-widest px-2 py-0.5 ${
                    lead.email_status === "verified"
                      ? "bg-[#CCFF00] text-black"
                      : lead.email_status === "accept_all"
                      ? "border border-[#CCFF00] text-[#CCFF00]"
                      : lead.email_status === "webmail"
                      ? "border border-[#FFB800] text-[#FFB800]"
                      : "border border-[#8a8a8a] text-[#8a8a8a]"
                  }`}
                  title={`Hunter confidence: ${lead.email_score || 0}`}
                  data-testid="email-status-badge"
                >
                  {lead.email_status || "unverified"} · {lead.email_score || 0}
                </span>
              </div>
            ) : (
              <div className="text-sm text-[#555] mt-1">—</div>
            )}
          </div>
          <Meta
            k="LinkedIn"
            v={
              lead.linkedin ? (
                <a
                  href={lead.linkedin.startsWith("http") ? lead.linkedin : `https://${lead.linkedin}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="lead-linkedin-link"
                  className="text-[#CCFF00] hover:underline break-all"
                >
                  ↗ open
                </a>
              ) : (
                "—"
              )
            }
          />
          <div className="py-2 border-b border-[#222]">
            <div className="flex items-baseline justify-between gap-2">
              <span className="uppercase-mono text-[#8a8a8a]">Website</span>
              {lead.website && (
                <a
                  href={lead.website.startsWith("http") ? lead.website : `https://${lead.website}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="lead-website-link"
                  className="uppercase-mono text-[#CCFF00] hover:underline"
                >
                  ↗ open
                </a>
              )}
            </div>
            <input
              type="text"
              value={lead.website || ""}
              onChange={(e) => setLead({ ...lead, website: e.target.value })}
              onBlur={async () => {
                try {
                  await updateLead(id, { website: (lead.website || "").trim() });
                  toast.success("Website saved");
                } catch {
                  toast.error("Could not save website");
                }
              }}
              placeholder="ralphlauren.com"
              data-testid="lead-website-input"
              className="w-full px-2 py-1 mt-1 text-sm font-mono bg-transparent border-0 border-b border-[#222] focus:border-[#CCFF00] focus:outline-none"
            />
          </div>
          <div className="uppercase-mono text-[#8a8a8a] mt-5 mb-2">Stage</div>
          <select
            value={lead.stage}
            onChange={(e) => onStageChange(e.target.value)}
            data-testid="lead-stage-select"
            className="w-full px-3 py-3 text-sm bg-transparent"
          >
            {STAGES.map((s) => (
              <option key={s.id} value={s.id} className="bg-[#0A0A0A]">
                {s.label}
              </option>
            ))}
          </select>
        </div>
      </header>

      {/* Body */}
      <section className="grid grid-cols-1 lg:grid-cols-12">
        {/* Left column: Outreach */}
        <div className="lg:col-span-8 p-12 border-r border-[#222]">
          <div className="flex items-end justify-between">
            <div>
              <div className="uppercase-mono text-[#8a8a8a]">Outreach</div>
              <h2 className="font-serif text-3xl mt-1">First touch · cold email</h2>
            </div>
            <button
              onClick={onGenerateDraft}
              disabled={draftLoading}
              data-testid="generate-draft-btn"
              className="btn-accent px-5 py-3 uppercase tracking-widest text-[11px] inline-flex items-center gap-2 disabled:opacity-50"
            >
              {draftLoading ? <Loader2 size={14} className="animate-spin" /> : <Wand2 size={14} strokeWidth={1.5} />}
              {lead.draft_email ? "Re-generate" : "Generate draft"}
            </button>
          </div>

          {lead.draft_email ? (
            <div className="mt-6 border border-[#222]" data-testid="draft-block">
              <div className="px-5 py-3 border-b border-[#222] flex items-center justify-between">
                <div className="uppercase-mono text-[#8a8a8a]">Subject</div>
                <div className="flex items-center gap-4">
                  {!draftEditing && (
                    <button
                      onClick={onEditDraft}
                      data-testid="draft-edit-btn"
                      className="uppercase-mono text-[#8a8a8a] hover:text-[#CCFF00] inline-flex items-center gap-1"
                    >
                      <Pencil size={12} /> edit
                    </button>
                  )}
                  <button
                    onClick={() => onCopy(`Subject: ${lead.draft_email.subject}\n\n${lead.draft_email.body}`)}
                    data-testid="draft-copy-btn"
                    className="uppercase-mono text-[#CCFF00] inline-flex items-center gap-1"
                  >
                    <Copy size={12} /> copy
                  </button>
                </div>
              </div>

              {draftEditing ? (
                <>
                  <input
                    type="text"
                    value={draftSubject}
                    onChange={(e) => setDraftSubject(e.target.value)}
                    data-testid="draft-subject-input"
                    placeholder="Subject"
                    className="w-full px-5 py-4 font-mono text-[#ededed] bg-transparent border-0 border-b border-[#222] focus:outline-none"
                  />
                  <textarea
                    value={draftBody}
                    onChange={(e) => setDraftBody(e.target.value)}
                    rows={Math.max(12, draftBody.split("\n").length + 2)}
                    data-testid="draft-body-input"
                    placeholder="Body"
                    className="w-full px-5 py-5 text-sm leading-relaxed font-mono bg-transparent border-0 focus:outline-none whitespace-pre-wrap resize-y"
                  />
                  <div className="px-5 py-3 border-t border-[#222] flex items-center justify-end gap-2">
                    <button
                      onClick={onCancelDraft}
                      data-testid="draft-cancel-btn"
                      className="btn-ghost px-3 py-2 uppercase tracking-widest text-[11px] inline-flex items-center gap-2"
                    >
                      <X size={12} strokeWidth={1.5} /> cancel
                    </button>
                    <button
                      onClick={onSaveDraft}
                      disabled={draftSaving}
                      data-testid="draft-save-btn"
                      className="btn-accent px-4 py-2 uppercase tracking-widest text-[11px] inline-flex items-center gap-2 disabled:opacity-50"
                    >
                      {draftSaving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} strokeWidth={1.5} />}
                      Save edits
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="px-5 py-4 font-mono text-[#ededed]" data-testid="draft-subject">
                    {lead.draft_email.subject}
                  </div>
                  <div className="px-5 py-5 border-t border-[#222] whitespace-pre-wrap text-sm text-[#ededed] leading-relaxed" data-testid="draft-body">
                    {lead.draft_email.body}
                  </div>
                </>
              )}
              <div className="px-5 py-4 border-t border-[#222] grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
                <div className="md:col-span-7">
                  <div className="uppercase-mono text-[#8a8a8a] mb-2">Send to</div>
                  <input
                    type="email"
                    value={toAddr}
                    onChange={(e) => setToAddr(e.target.value)}
                    placeholder="name@company.com"
                    data-testid="send-to-input"
                    className="w-full px-3 py-2 text-sm font-mono"
                  />
                </div>
                <div className="md:col-span-5 flex md:justify-end gap-2">
                  <button
                    onClick={onSendNow}
                    disabled={sendLoading || draftEditing}
                    data-testid="send-now-btn"
                    title={draftEditing ? "Save your edits before sending" : ""}
                    className="btn-accent px-4 py-2 uppercase tracking-widest text-[11px] inline-flex items-center gap-2 disabled:opacity-50"
                  >
                    {sendLoading ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} strokeWidth={1.5} />}
                    {sendLoading ? "sending…" : "Send now"}
                  </button>
                  <button
                    onClick={onMarkSent}
                    data-testid="mark-sent-btn"
                    className="btn-ghost px-3 py-2 uppercase tracking-widest text-[11px] inline-flex items-center gap-2"
                    title="Log a manual send (sent outside the app)"
                  >
                    log manual
                  </button>
                </div>
              </div>
              <div className="px-5 py-3 border-t border-[#222] uppercase-mono text-[#555]">
                generated · {new Date(lead.draft_email.generated_at).toLocaleString()} · from adicreativetechnologist@gmail.com
              </div>
            </div>
          ) : (
            <div className="mt-6 border border-dashed border-[#222] p-10 text-center" data-testid="draft-empty">
              <div className="font-serif text-2xl text-[#8a8a8a]">No draft yet.</div>
              <div className="uppercase-mono text-[#555] mt-2">Click generate to write the first touch</div>
            </div>
          )}

          {/* Sequence */}
          <div className="flex items-end justify-between mt-12">
            <div>
              <div className="uppercase-mono text-[#8a8a8a]">Nurture</div>
              <h2 className="font-serif text-3xl mt-1">Follow-up sequence · 3 steps</h2>
            </div>
            <button
              onClick={onGenerateSeq}
              disabled={seqLoading}
              data-testid="generate-sequence-btn"
              className="btn-ghost px-5 py-3 uppercase tracking-widest text-[11px] inline-flex items-center gap-2"
            >
              {seqLoading ? <Loader2 size={14} className="animate-spin" /> : <Calendar size={14} strokeWidth={1.5} />}
              {lead.sequence ? "Re-generate" : "Generate sequence"}
            </button>
          </div>

          {lead.sequence?.length ? (
            <div className="mt-6 space-y-px bg-[#222]" data-testid="sequence-block">
              {lead.sequence.map((step) => {
                const editing = seqEditingStep === step.step;
                return (
                  <div key={step.step} className="bg-[#0A0A0A] p-5">
                    <div className="flex items-baseline justify-between">
                      <div className="font-serif text-2xl">Step {step.step}</div>
                      <div className="uppercase-mono text-[#8a8a8a]">
                        {step.channel} · day +{step.day_offset}
                      </div>
                    </div>

                    {editing ? (
                      <>
                        <textarea
                          value={seqBody}
                          onChange={(e) => setSeqBody(e.target.value)}
                          rows={Math.max(6, seqBody.split("\n").length + 1)}
                          data-testid={`step-${step.step}-body-input`}
                          className="w-full mt-3 px-3 py-3 text-sm font-mono leading-relaxed resize-y"
                        />
                        <div className="mt-3 flex items-center gap-2">
                          <button
                            onClick={() => onSaveStep(step)}
                            disabled={seqSaving}
                            data-testid={`save-step-${step.step}`}
                            className="btn-accent px-4 py-2 uppercase tracking-widest text-[11px] inline-flex items-center gap-2 disabled:opacity-50"
                          >
                            {seqSaving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} strokeWidth={1.5} />}
                            save
                          </button>
                          <button
                            onClick={onCancelStep}
                            data-testid={`cancel-step-${step.step}`}
                            className="btn-ghost px-3 py-2 uppercase tracking-widest text-[11px] inline-flex items-center gap-2"
                          >
                            <X size={12} strokeWidth={1.5} /> cancel
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="whitespace-pre-wrap text-sm mt-3 text-[#ededed] leading-relaxed">{step.body}</div>
                        <div className="mt-3 flex items-center gap-4">
                          <button
                            onClick={() => onCopy(step.body)}
                            data-testid={`copy-step-${step.step}`}
                            className="uppercase-mono text-[#CCFF00] inline-flex items-center gap-1"
                          >
                            <Copy size={12} /> copy
                          </button>
                          <button
                            onClick={() => onEditStep(step)}
                            data-testid={`edit-step-${step.step}`}
                            className="uppercase-mono text-[#8a8a8a] hover:text-[#CCFF00] inline-flex items-center gap-1"
                          >
                            <Pencil size={12} /> edit
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="mt-6 border border-dashed border-[#222] p-8 text-center">
              <div className="uppercase-mono text-[#555]">No sequence yet.</div>
            </div>
          )}

          {/* LinkedIn connector */}
          <div className="flex items-end justify-between mt-12">
            <div>
              <div className="uppercase-mono text-[#8a8a8a]">Connect</div>
              <h2 className="font-serif text-3xl mt-1">LinkedIn · connection request</h2>
            </div>
            <button
              onClick={onGenerateLinkedin}
              disabled={liLoading}
              data-testid="generate-linkedin-btn"
              className="btn-ghost px-5 py-3 uppercase tracking-widest text-[11px] inline-flex items-center gap-2"
            >
              {liLoading ? <Loader2 size={14} className="animate-spin" /> : <Linkedin size={14} strokeWidth={1.5} />}
              {lead.linkedin_note ? "Re-generate" : "Generate connect note"}
            </button>
          </div>

          {lead.linkedin_note ? (
            <div className="mt-6 border border-[#222]" data-testid="linkedin-block">
              <div className="px-5 py-3 border-b border-[#222] flex items-center justify-between">
                <div className="uppercase-mono text-[#8a8a8a]">
                  Note · <span className={lead.linkedin_note.char_count > 280 ? "text-[#FF3333]" : "text-[#CCFF00]"}>
                    {liEditing ? liBody.length : lead.linkedin_note.char_count}/300
                  </span>
                </div>
                <div className="flex items-center gap-4">
                  {!liEditing && (
                    <button
                      onClick={onEditLinkedin}
                      data-testid="linkedin-edit-btn"
                      className="uppercase-mono text-[#8a8a8a] hover:text-[#CCFF00] inline-flex items-center gap-1"
                    >
                      <Pencil size={12} /> edit
                    </button>
                  )}
                </div>
              </div>

              {liEditing ? (
                <>
                  <textarea
                    value={liBody}
                    onChange={(e) => setLiBody(e.target.value)}
                    rows={5}
                    maxLength={300}
                    data-testid="linkedin-body-input"
                    className="w-full px-5 py-5 text-sm leading-relaxed font-mono bg-transparent border-0 focus:outline-none resize-y"
                  />
                  <div className="px-5 py-3 border-t border-[#222] flex items-center justify-end gap-2">
                    <button
                      onClick={onCancelLinkedin}
                      data-testid="linkedin-cancel-btn"
                      className="btn-ghost px-3 py-2 uppercase tracking-widest text-[11px] inline-flex items-center gap-2"
                    >
                      <X size={12} strokeWidth={1.5} /> cancel
                    </button>
                    <button
                      onClick={onSaveLinkedin}
                      disabled={liSaving}
                      data-testid="linkedin-save-btn"
                      className="btn-accent px-4 py-2 uppercase tracking-widest text-[11px] inline-flex items-center gap-2 disabled:opacity-50"
                    >
                      {liSaving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} strokeWidth={1.5} />}
                      Save
                    </button>
                  </div>
                </>
              ) : (
                <div className="px-5 py-5 whitespace-pre-wrap text-sm text-[#ededed] leading-relaxed" data-testid="linkedin-body">
                  {lead.linkedin_note.body}
                </div>
              )}

              <div className="px-5 py-4 border-t border-[#222] flex flex-wrap items-center gap-2 justify-between">
                <div className="uppercase-mono text-[#555] truncate max-w-[55%]">
                  route · {lead.linkedin_note.profile_url}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={onCopyAndOpenLinkedin}
                    disabled={liEditing}
                    data-testid="linkedin-open-btn"
                    className="btn-accent px-4 py-2 uppercase tracking-widest text-[11px] inline-flex items-center gap-2 disabled:opacity-50"
                  >
                    <ExternalLink size={14} strokeWidth={1.5} />
                    Copy note &amp; open profile
                  </button>
                  <button
                    onClick={onMarkConnected}
                    data-testid="linkedin-mark-sent-btn"
                    className="btn-ghost px-3 py-2 uppercase tracking-widest text-[11px] inline-flex items-center gap-2"
                    title="Log that you sent the connection request"
                  >
                    mark connected
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="mt-6 border border-dashed border-[#222] p-8 text-center" data-testid="linkedin-empty">
              <div className="uppercase-mono text-[#555]">
                {lead.linkedin
                  ? "No connect note yet. Generate one tailored to this prospect."
                  : "No LinkedIn URL on file — note will use a name+company search link instead."}
              </div>
            </div>
          )}
        </div>

        {/* Right column: Activity */}
        <aside className="lg:col-span-4 p-12">
          <div className="uppercase-mono text-[#8a8a8a]">Activity</div>
          <h2 className="font-serif text-3xl mt-1">Touch timeline</h2>

          <div className="mt-6">
            <textarea
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Add a note about this prospect…"
              data-testid="note-input"
              className="w-full px-3 py-3 text-sm"
            />
            <button onClick={onAddNote} data-testid="add-note-btn" className="btn-ghost mt-3 px-4 py-2 uppercase tracking-widest text-[11px] inline-flex items-center gap-2">
              <MessageSquare size={12} strokeWidth={1.5} /> add note
            </button>
          </div>

          <ol className="mt-8 space-y-5" data-testid="activity-list">
            {activities.length === 0 && (
              <li className="text-[#555] uppercase-mono">no activity yet</li>
            )}
            {activities.map((a) => (
              <li key={a.id} className="border-l-2 border-[#CCFF00] pl-4">
                <div className="uppercase-mono text-[#8a8a8a]">
                  {a.kind} · {new Date(a.created_at).toLocaleString()}
                </div>
                <div className="text-sm text-[#ededed] mt-1 whitespace-pre-wrap">{a.body}</div>
              </li>
            ))}
          </ol>
        </aside>
      </section>
    </div>
  );
}

function Meta({ k, v }) {
  return (
    <div className="flex items-baseline justify-between py-2 border-b border-[#222]">
      <span className="uppercase-mono text-[#8a8a8a]">{k}</span>
      <span className="text-sm text-[#ededed] truncate ml-3 max-w-[60%] text-right">{v}</span>
    </div>
  );
}
